import React from 'react';

import styled from '@emotion/styled';

import { getLuminance } from 'polished';

import {
  disabledColor,
  primaryColor,
  unit,
  unitTRBL,
  white,
} from '@zego/theme';

import Link from '@zego/components/Link';

/**
 * @param {Object} props
 * @param {Object} [props.theme]
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.to]
 * @param {string} [props.href]
 * @param {string} [props.className]
 * @param {string | React.ReactNode} [props.icon]
 * @param {boolean} [props.external]
 * @param {string} [props.download]
 * @param {string} [props.target]
 * @param {React.MouseEventHandler<HTMLAnchorElement>} [props.onClick]
 * @param {string} [props.backgroundColor]
 */
const ButtonLink = ({
  children,
  to,
  href,
  className,
  icon,
  external,
  ...rest
}) => (
  <Link
    to={to}
    href={href}
    external={external}
    className={className}
    {...rest}
  >
    {icon ? (
      typeof icon === 'string' ? (
        <img alt="" src={icon} css={{ paddingRight: unit(1) }} />
      ) : (
        <div
          css={{
            paddingRight: unit(1),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </div>
      )
    ) : null}
    {children}
  </Link>
);

const StyledButton = styled(ButtonLink, {
  shouldForwardProp: prop => ['backgroundColor'].indexOf(prop) === -1,
})(
  {
    display: 'inline-flex',
    flexDirection: 'row',
    border: 'none',
    borderRadius: '8px',
    padding: unitTRBL(2, 4, 2, 4),
    lineHeight: '24px',
    transition: '0.1s ease all',
    fontSize: 18,
    textDecoration: 'none',
    cursor: 'pointer',
    position: 'relative',
    minWidth: 100,
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 500,
    textAlign: 'center',
    ':hover, :focus': {
      transform: 'scale(1.025)',
    },
    ':active': {
      transform: 'scale(0.975)',
    },
  },
  ({ color, backgroundColor, theme }) => {
    const actualBackgroundColor =
      backgroundColor || theme.tintColor || primaryColor();

    return {
      backgroundColor: actualBackgroundColor,
      color: color
        ? color
        : getLuminance(actualBackgroundColor) > 0.7
        ? disabledColor(10)
        : white,
    };
  },
);

export default StyledButton;
